import "@/App.css";
import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Instagram, 
  Facebook, 
  ShoppingCart, 
  Heart,
  Globe,
  Book,
  Users,
  ExternalLink,
  Menu,
  X,
  Mail,
  Mic,
  Video,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Package,
  Loader2
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Media URLs - Updated with new book cover
const MEDIA = {
  heroBg: "https://images.pexels.com/photos/19191597/pexels-photo-19191597.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536",
  bookCover: "https://customer-assets.emergentagent.com/job_aloha-cancer-hub/artifacts/3p8cr7jw_Cancer%20I%20Can%20Move%20Mountains%20Cover%20Final%20-%20Copy.png",
  danielGray: "https://static.wixstatic.com/media/a48143_10a1fe72f7d3465dab7ac5ba13687bfa~mv2.png",
  markBecker: "https://customer-assets.emergentagent.com/job_aloha-cancer-hub/artifacts/5z2wrcjw_DSC09654.JPG",
  christaWittmier: "https://static.wixstatic.com/media/a48143_f0d827153bea469aba02763663e14232~mv2.jpg",
};

// Social Links
const SOCIAL_LINKS = {
  bookInstagram: "https://instagram.com/cancericanmovemountains",
  bookFacebook: "https://facebook.com/cancericanmovemountains",
  acpInstagram: "https://instagram.com/alohacancerproject",
  acpFacebook: "https://facebook.com/alohacancerproject",
  acpWebsite: "https://www.alohacancerproject.com"
};

// Static Data
const FEATURES = [
  { id: "dual-perspective", icon: Book, title: "Dual Perspective", desc: "Insights from both a Stage IV cancer warrior and a dedicated caretaker" },
  { id: "faith-hope", icon: Heart, title: "Faith & Hope", desc: "Spiritual guidance and emotional support through life's most challenging moments" },
  { id: "practical-guide", icon: Users, title: "Practical Guide", desc: "Real solutions and strategies for patients, caregivers, and families" }
];

const AUTHORS = [
  {
    id: "christa-wittmier",
    name: "Christa 'Super CW' Wittmier",
    role: "Cancer Warrior & Co-Author",
    image: MEDIA.christaWittmier,
    bio: "Christa was a warrior, a beacon of hope, and an inspiration to all who knew her. Her journey through Stage IV breast cancer was marked by unwavering faith, incredible strength, and a determination to help others facing similar battles. A US Navy veteran, DJ, writer, artist, and founder of the Aloha Cancer Project, her message continues to inspire hope and provide comfort to those who need it most."
  },
  {
    id: "daniel-gray",
    name: "Daniel Gray",
    role: "Inspiration & Aloha Cancer Project",
    image: MEDIA.danielGray,
    bio: "Daniel Gray was a nightlife mogul, an avid lover of books, nature, and the ocean. His smile and laugh could light up a room. He dreamed large and could do anything he put his mind to. When a brain tumor changed everything, he was given 6 months to live but fought as a warrior for 3 years. His legacy lives on through the Aloha Cancer Project, helping countless others with resources and hope."
  },
  {
    id: "mark-becker",
    name: "Mark Becker",
    role: "Caretaker & Co-Author",
    image: MEDIA.markBecker,
    bio: "Mark's journey as a caretaker is one of profound love, sacrifice, and unwavering dedication. Best friends with Daniel, Mark experienced firsthand the challenges, fears, and triumphs of caring for loved ones facing life-threatening illness. His perspective brings a unique and essential voice to this book, demonstrating that caretakers are also warriors in the battle against cancer."
  }
];

const FAQS = [
  { id: "faq-about", question: "What is 'Cancer: I Can Move Mountains' about?", answer: "This book is a dual-perspective memoir chronicling the profound journey of Christa Wittmier, who battled Stage IV breast cancer, and her friend and caregiver, Mark Becker. It offers authentic storytelling, faith-anchored wisdom, practical guidance, and emotional support for those navigating the cancer journey." },
  { id: "faq-purchase", question: "Where can I purchase the book?", answer: "You can purchase the book directly from this website for $17.77. Simply click the 'Order Now' button and complete checkout securely via Stripe. You'll enter your shipping address during checkout, and the book ships directly from the US within 5-7 business days." },
  { id: "faq-audience", question: "Is this book only for cancer patients?", answer: "Not at all! While the book is deeply relevant for cancer patients, it&apos;s also invaluable for caretakers, family members, friends, and anyone seeking inspiration about resilience, love, and faith in the face of life's greatest challenges." },
  { id: "faq-caretaker", question: "How can I support a loved one who is a cancer caretaker?", answer: "This book provides practical insights into the caretaker's experience. Mark Becker shares raw emotions, solutions to unspoken problems, and the spiritual growth that comes with supporting someone through cancer. It's an excellent resource to understand and support the caretakers in your life." },
  { id: "faq-acp", question: "What is the Aloha Cancer Project?", answer: "The Aloha Cancer Project is a non-profit co-founded by Christa Wittmier and Daniel Gray to help cancer patients and caretakers in Hawaii. It provides resources, support, and a community for those affected by cancer. Visit alohacancerproject.com to learn more." },
  { id: "faq-speaking", question: "What kind of speaking engagements does Mark Becker offer?", answer: "Mark Becker is available for speaking engagements at conferences, churches, support groups, and organizations. He shares his personal journey as a caretaker and offers insights on resilience, faith, and supporting loved ones through cancer. Contact us through our contact page for inquiries." },
  { id: "faq-shipping", question: "How does shipping work?", answer: "Books ship directly from the US. During checkout, you'll enter your shipping address. After completing your purchase, your order will be processed and shipped within 5-7 business days. You will receive tracking information via email once your order ships." }
];

const OPPORTUNITIES = [
  { id: "opp-podcast", icon: Mic, title: "Podcasts", desc: "Share our story on your podcast" },
  { id: "opp-interview", icon: Video, title: "Interviews", desc: "Media and press interviews" },
  { id: "opp-inperson", icon: Users, title: "In-Person", desc: "Meet in person for interviews" },
  { id: "opp-speaking", icon: MessageSquare, title: "Speaking", desc: "Conferences & events" }
];

const INQUIRY_TYPES = [
  { value: 'podcast', label: 'Podcast Appearance', icon: Mic },
  { value: 'interview', label: 'Interview Request', icon: Video },
  { value: 'speaking', label: 'Speaking Engagement', icon: MessageSquare },
  { value: 'general', label: 'General Inquiry', icon: Mail }
];

// Reusable Components
const FeatureCard = ({ feature }) => (
  <div className="bg-[#F9F6F0] rounded-xl p-6 card-lift border border-[#E2DCD0]" data-testid={`feature-card-${feature.id}`}>
    <feature.icon className="text-[#8A9A86] mb-4" size={32} />
    <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">{feature.title}</h3>
    <p className="text-[#5C5C5C]">{feature.desc}</p>
  </div>
);

const AuthorCard = ({ author }) => (
  <div className="bg-[#FFFFFF] rounded-2xl overflow-hidden card-lift border border-[#E2DCD0]" data-testid={`author-card-${author.id}`}>
    <div className="aspect-square overflow-hidden bg-[#EFEBE1]">
      <img src={author.image} alt={author.name} className="w-full h-full bio-image" />
    </div>
    <div className="p-6 space-y-4">
      <div>
        <h3 className="text-2xl font-serif text-[#1A1A1A]">{author.name}</h3>
        <span className="text-[#8A9A86] font-medium text-sm">{author.role}</span>
      </div>
      <p className="text-[#5C5C5C] text-sm leading-relaxed">{author.bio}</p>
    </div>
  </div>
);

const FAQItem = ({ faq }) => (
  <AccordionItem 
    value={faq.id}
    className="bg-[#F9F6F0] rounded-xl border border-[#E2DCD0] px-6 overflow-hidden"
    data-testid={`faq-item-${faq.id}`}
  >
    <AccordionTrigger className="text-left font-serif text-lg md:text-xl text-[#1A1A1A] py-5 hover:no-underline">
      {faq.question}
    </AccordionTrigger>
    <AccordionContent className="text-[#5C5C5C] pb-5 leading-relaxed">
      {faq.answer}
    </AccordionContent>
  </AccordionItem>
);

const OpportunityCard = ({ item }) => (
  <div className="bg-[#F9F6F0] rounded-xl p-6 text-center card-lift border border-[#E2DCD0]" data-testid={`opportunity-card-${item.id}`}>
    <div className="w-16 h-16 bg-[#E07A5F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
      <item.icon className="text-[#E07A5F]" size={28} />
    </div>
    <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">{item.title}</h3>
    <p className="text-sm text-[#5C5C5C]">{item.desc}</p>
  </div>
);

const InquiryTypeButton = ({ type, isSelected, onChange }) => (
  <label
    className={`flex flex-col items-center gap-2 p-4 rounded-xl border cursor-pointer transition-all ${
      isSelected ? 'border-[#E07A5F] bg-[#E07A5F]/10' : 'border-[#E2DCD0] bg-[#F9F6F0] hover:border-[#8A9A86]'
    }`}
    data-testid={`inquiry-type-${type.value}`}
  >
    <input type="radio" name="inquiry_type" value={type.value} checked={isSelected} onChange={onChange} className="sr-only" required />
    <type.icon size={24} className={isSelected ? 'text-[#E07A5F]' : 'text-[#8A9A86]'} />
    <span className="text-xs text-center font-medium">{type.label}</span>
  </label>
);

const SocialLinkGroup = ({ title, subtitle, instagramUrl, facebookUrl, testIdPrefix }) => (
  <div className="space-y-6">
    <h4 className="font-serif text-xl">{title}</h4>
    <div className="flex gap-4">
      <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-icon text-[#8A9A86] hover:text-[#E07A5F]" data-testid={`social-link-instagram-${testIdPrefix}`}>
        <Instagram size={28} />
      </a>
      <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-icon text-[#8A9A86] hover:text-[#E07A5F]" data-testid={`social-link-facebook-${testIdPrefix}`}>
        <Facebook size={28} />
      </a>
    </div>
    <p className="text-sm text-[#8A9A86]">{subtitle}</p>
  </div>
);

// Navigation Component
const Navbar = ({ isHomePage = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = useCallback((id) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  }, [isHomePage, navigate]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass bg-[#F9F6F0]/80 border-b border-[#E2DCD0]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="font-serif text-xl md:text-2xl text-[#1A1A1A] tracking-tight" data-testid="logo">
            Cancer: I Can Move Mountains
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors link-underline" data-testid="nav-about">About the Book</button>
            <button onClick={() => scrollToSection('authors')} className="text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors link-underline" data-testid="nav-authors">The Authors</button>
            <Link to="/contact" className="text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors link-underline" data-testid="nav-contact">Contact</Link>
            <button onClick={() => scrollToSection('faq')} className="text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors link-underline" data-testid="nav-faq">FAQ</button>
            <button onClick={() => scrollToSection('hero')} className="btn-primary px-6 py-2.5 rounded-full font-medium flex items-center gap-2" data-testid="nav-buy-button">
              <ShoppingCart size={18} />Order Now - $17.77
            </button>
          </div>

          <button className="md:hidden text-[#1A1A1A] p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-button">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#E2DCD0]" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('about')} className="text-left text-[#5C5C5C] hover:text-[#1A1A1A] py-2">About the Book</button>
              <button onClick={() => scrollToSection('authors')} className="text-left text-[#5C5C5C] hover:text-[#1A1A1A] py-2">The Authors</button>
              <Link to="/contact" onClick={() => setIsOpen(false)} className="text-left text-[#5C5C5C] hover:text-[#1A1A1A] py-2">Contact</Link>
              <button onClick={() => scrollToSection('faq')} className="text-left text-[#5C5C5C] hover:text-[#1A1A1A] py-2">FAQ</button>
              <button onClick={() => scrollToSection('hero')} className="btn-primary px-6 py-3 rounded-full font-medium flex items-center justify-center gap-2 mt-2">
                <ShoppingCart size={18} />Order Now - $17.77
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleOrderClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <section id="hero" className="hero-bg min-h-screen pt-20 flex items-center" style={{ backgroundImage: `url(${MEDIA.heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} data-testid="hero-section">
      <div className="hero-content w-full py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <HeroContent onOrderClick={handleOrderClick} isLoading={isLoading} />
            <HeroBookCover />
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroContent = ({ onOrderClick, isLoading }) => (
  <div className="space-y-8 animate-fade-in-up">
    <span className="inline-block text-[#E07A5F] font-medium tracking-wider uppercase text-sm">A Story of Love, Loss & Redemption</span>
    <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A] leading-tight">
      Cancer: I Can <br /><span className="italic text-[#8A9A86]">Move Mountains</span>
    </h1>
    <p className="text-lg md:text-xl text-[#5C5C5C] max-w-xl leading-relaxed">
      A raw, heartfelt journey through love, loss, and redemption. Written by Christa &ldquo;Super CW&rdquo; Wittmier and Mark Becker.
    </p>
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <span className="text-4xl font-serif text-[#1A1A1A]">$17.77</span>
        <span className="shipping-badge flex items-center gap-2"><Globe size={16} />Ships from US directly</span>
      </div>
      <button 
        onClick={onOrderClick} 
        disabled={isLoading}
        className="btn-primary px-8 py-4 rounded-full text-lg font-medium flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" 
        data-testid="order-now-button"
      >
        {isLoading ? (
          <>
            <Loader2 size={22} className="animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard size={22} />
            Order Now - Secure Checkout
          </>
        )}
      </button>
      <HowToPurchase />
    </div>
  </div>
);

const HowToPurchase = () => (
  <div className="bg-[#FFFFFF]/80 backdrop-blur rounded-xl p-4 border border-[#E2DCD0] max-w-md" data-testid="how-to-buy-info">
    <h4 className="font-medium text-[#1A1A1A] mb-2 flex items-center gap-2"><Book size={18} />How to Purchase</h4>
    <ul className="text-sm text-[#5C5C5C] space-y-1">
      <li>1. Click &ldquo;Order Now&rdquo; to start secure checkout</li>
      <li>2. Enter your shipping address and payment info</li>
      <li>3. Book ships directly from US within 5-7 business days</li>
    </ul>
  </div>
);

const HeroBookCover = () => (
  <div className="flex justify-center lg:justify-end animate-fade-in-up delay-200">
    <div className="relative">
      <img src={MEDIA.bookCover} alt="Cancer: I Can Move Mountains Book Cover" className="w-80 md:w-96 book-shadow rounded-lg" data-testid="book-cover-image" />
      <div className="absolute -bottom-4 -right-4 bg-[#E07A5F] text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">Best Seller</div>
    </div>
  </div>
);

// Synopsis Section
const SynopsisSection = () => (
  <section id="about" className="py-20 md:py-32 bg-[#FFFFFF]" data-testid="synopsis-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <SynopsisContent />
        <QuoteCard />
      </div>
      <div className="grid md:grid-cols-3 gap-8 mt-20">
        {FEATURES.map((feature) => <FeatureCard key={feature.id} feature={feature} />)}
      </div>
    </div>
  </section>
);

const SynopsisContent = () => (
  <div className="space-y-8">
    <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">About the Book</span>
    <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">More Than a Memoir—A Beacon of Hope</h2>
    <div className="space-y-6 text-[#5C5C5C] text-lg leading-relaxed">
      <p>&ldquo;Cancer: I Can Move Mountains&rdquo; is a raw, heartfelt journey through love, loss, and redemption. This powerful narrative chronicles the experiences of Christa &ldquo;Super CW&rdquo; Wittmier and Mark Becker with cancer and the unwavering faith, will, and love that sustained them.</p>
      <p>Whether you&apos;re a patient seeking encouragement, a caretaker needing strength, or a community wanting to better support those affected by cancer, this book provides the tools, inspiration, and hope you need to move mountains.</p>
    </div>
  </div>
);

const QuoteCard = () => (
  <div className="bg-[#EFEBE1] rounded-2xl p-8 md:p-12 space-y-6" data-testid="quote-card">
    <blockquote className="quote-text">&ldquo;Through authentic storytelling and faith-anchored wisdom, it offers practical guidance and emotional support for those navigating the cancer journey.&rdquo;</blockquote>
    <div className="flex items-center gap-4">
      <Heart className="text-[#E07A5F]" size={24} />
      <span className="font-medium text-[#1A1A1A]">For patients, caretakers, and families</span>
    </div>
  </div>
);

// Authors Section
const AuthorsSection = () => (
  <section id="authors" className="py-20 md:py-32 bg-[#F9F6F0]" data-testid="authors-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-16">
        <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">Meet the Authors</span>
        <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] mt-4">The Warriors Behind the Story</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {AUTHORS.map((author) => <AuthorCard key={author.id} author={author} />)}
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => (
  <section id="faq" className="py-20 md:py-32 bg-[#FFFFFF]" data-testid="faq-section">
    <div className="max-w-4xl mx-auto px-6 md:px-12">
      <div className="text-center mb-16">
        <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">Have Questions?</span>
        <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] mt-4">Frequently Asked Questions</h2>
      </div>
      <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
        {FAQS.map((faq) => <FAQItem key={faq.id} faq={faq} />)}
      </Accordion>
    </div>
  </section>
);

// Footer Section
const Footer = () => (
  <footer className="bg-[#1C3325] text-[#F9F6F0] py-16 md:py-24" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        <FooterBrand />
        <SocialLinkGroup title="Follow the Book" subtitle="@cancericanmovemountains" instagramUrl={SOCIAL_LINKS.bookInstagram} facebookUrl={SOCIAL_LINKS.bookFacebook} testIdPrefix="book" />
        <SocialLinkGroup title="Aloha Cancer Project" subtitle="@alohacancerproject" instagramUrl={SOCIAL_LINKS.acpInstagram} facebookUrl={SOCIAL_LINKS.acpFacebook} testIdPrefix="acp" />
      </div>
      <FooterBottom />
    </div>
  </footer>
);

const FooterBrand = () => (
  <div className="lg:col-span-2 space-y-6">
    <h3 className="font-serif text-3xl md:text-4xl leading-tight">Cancer: I Can <br />Move Mountains</h3>
    <p className="text-[#8A9A86] max-w-md">A story of love, loss, and redemption. Supporting cancer patients and caretakers through authentic storytelling and faith-anchored wisdom.</p>
    <div className="flex gap-4">
      <a href={SOCIAL_LINKS.acpWebsite} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#8A9A86] hover:bg-[#E07A5F] text-white px-6 py-3 rounded-full transition-colors" data-testid="acp-website-link">
        <Globe size={18} />Aloha Cancer Project<ExternalLink size={16} />
      </a>
      <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#c96a52] text-white px-6 py-3 rounded-full transition-colors" data-testid="footer-contact-link">
        <Mail size={18} />Contact Us
      </Link>
    </div>
  </div>
);

const FooterBottom = () => (
  <div className="border-t border-[#2a4a3a] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
    <p className="text-[#8A9A86] text-sm">© {new Date().getFullYear()} Cancer: I Can Move Mountains. All rights reserved.</p>
    <p className="text-[#8A9A86] text-sm flex items-center gap-2"><Heart size={14} className="text-[#E07A5F]" />In loving memory of Christa & Daniel</p>
  </div>
);

// Order Success Page
const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [orderStatus, setOrderStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shippingSubmitted, setShippingSubmitted] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (!sessionId) {
        setOrderStatus('error');
        return;
      }

      if (attempts >= maxAttempts) {
        setOrderStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setOrderStatus('success');
          setOrderDetails(data);
          setShowShippingForm(true);
          return;
        } else if (data.status === 'expired') {
          setOrderStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setOrderStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Navbar isHomePage={false} />
      
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {orderStatus === 'loading' && (
            <div className="text-center space-y-6" data-testid="order-loading">
              <Loader2 size={64} className="animate-spin text-[#E07A5F] mx-auto" />
              <h1 className="text-3xl md:text-4xl font-serif text-[#1A1A1A]">Processing Your Order...</h1>
              <p className="text-[#5C5C5C]">Please wait while we confirm your payment.</p>
            </div>
          )}

          {orderStatus === 'success' && !shippingSubmitted && (
            <div className="space-y-8" data-testid="order-success">
              <div className="text-center">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle size={48} className="text-green-600" />
                </div>
                <h1 className="text-3xl md:text-4xl font-serif text-[#1A1A1A] mt-6">Payment Successful!</h1>
                <p className="text-[#5C5C5C] text-lg mt-4">Please provide your shipping address below so we can send your book.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-[#E2DCD0] space-y-2">
                <h3 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2">
                  <Package size={20} className="text-[#8A9A86]" />
                  Order Summary
                </h3>
                <div className="text-[#5C5C5C] text-sm">
                  <p><strong>Product:</strong> Cancer: I Can Move Mountains</p>
                  <p><strong>Amount:</strong> ${(orderDetails?.amount_total / 100).toFixed(2) || '17.77'} {orderDetails?.currency?.toUpperCase() || 'USD'}</p>
                  <p><strong>Payment:</strong> <span className="text-green-600 font-medium">Completed</span></p>
                </div>
              </div>

              {showShippingForm && (
                <ShippingForm sessionId={sessionId} onSuccess={() => setShippingSubmitted(true)} />
              )}
            </div>
          )}

          {orderStatus === 'success' && shippingSubmitted && (
            <div className="text-center space-y-8" data-testid="shipping-complete">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Package size={48} className="text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-[#1A1A1A]">Order Complete!</h1>
              <p className="text-[#5C5C5C] text-lg">Thank you for your order! Your copy of &ldquo;Cancer: I Can Move Mountains&rdquo; will be shipped within 5-7 business days.</p>
              
              <div className="bg-[#8A9A86]/10 rounded-2xl p-6 border border-[#8A9A86]/20">
                <p className="text-[#1A1A1A]">You will receive a confirmation email with tracking information once your order ships.</p>
              </div>

              <Link to="/" className="btn-primary px-8 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2">
                <Book size={20} />
                Back to Home
              </Link>
            </div>
          )}

          {(orderStatus === 'error' || orderStatus === 'expired' || orderStatus === 'timeout') && (
            <div className="text-center space-y-8" data-testid="order-error">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle size={48} className="text-red-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-[#1A1A1A]">
                {orderStatus === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
              </h1>
              <p className="text-[#5C5C5C] text-lg">
                {orderStatus === 'expired' 
                  ? 'Your checkout session has expired. Please try again.'
                  : 'We couldn\'t confirm your payment. If you were charged, please contact us.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" className="btn-primary px-8 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2">
                  Try Again
                </Link>
                <Link to="/contact" className="bg-[#8A9A86] hover:bg-[#7a8a76] text-white px-8 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 transition-colors">
                  Contact Support
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Shipping Form Component
const ShippingForm = ({ sessionId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/order/${sessionId}/shipping`, {
        session_id: sessionId,
        ...formData
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit shipping address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, sessionId, onSuccess]);

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="bg-white rounded-2xl p-8 border border-[#E2DCD0] shadow-lg" data-testid="shipping-form">
      <h3 className="font-serif text-2xl text-[#1A1A1A] mb-6 flex items-center gap-2">
        <Globe size={24} className="text-[#E07A5F]" />
        Shipping Address
      </h3>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-800 border border-red-200 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-600" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
              placeholder="John Doe"
              data-testid="shipping-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
              placeholder="john@example.com"
              data-testid="shipping-email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Street Address *</label>
          <input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
            placeholder="123 Main St"
            data-testid="shipping-address1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Apt, Suite, etc. (Optional)</label>
          <input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
            placeholder="Apt 4B"
            data-testid="shipping-address2"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
              placeholder="Honolulu"
              data-testid="shipping-city"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">State *</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
              data-testid="shipping-state"
            >
              <option value="">Select</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">ZIP Code *</label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F]"
              placeholder="96815"
              data-testid="shipping-zip"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Country</label>
          <input
            type="text"
            value="United States"
            disabled
            className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#EFEBE1] text-[#5C5C5C]"
          />
          <p className="text-xs text-[#8A9A86] mt-1">Currently shipping within the US only</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn-primary px-8 py-4 rounded-full text-lg font-medium flex items-center justify-center gap-3 mt-6 disabled:opacity-50"
          data-testid="submit-shipping"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Package size={20} />
              Complete Order
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// Contact Page Components
const ContactHero = () => (
  <section className="pt-32 pb-16 px-6 md:px-12">
    <div className="max-w-4xl mx-auto text-center">
      <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">Get in Touch</span>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#1A1A1A] mt-4 tracking-tight">Contact Us</h1>
      <p className="text-lg md:text-xl text-[#5C5C5C] mt-6 max-w-2xl mx-auto">We&apos;d love to hear from you! Whether it&apos;s about the book, speaking opportunities, or just to share your story.</p>
    </div>
  </section>
);

const OpportunitiesSection = () => (
  <section className="py-12 px-6 md:px-12 bg-[#FFFFFF]" data-testid="opportunities-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-serif text-[#1A1A1A] text-center mb-12">We&apos;re Open To</h2>
      <div className="grid md:grid-cols-4 gap-6">
        {OPPORTUNITIES.map((item) => <OpportunityCard key={item.id} item={item} />)}
      </div>
    </div>
  </section>
);

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', inquiry_type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', email: '', subject: '', inquiry_type: '', message: '' });
    } catch (error) {
      setSubmitStatus({ type: 'error', message: error.response?.data?.detail || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  return (
    <section className="py-16 px-6 md:px-12" data-testid="contact-form-section">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#FFFFFF] rounded-2xl p-8 md:p-12 shadow-lg border border-[#E2DCD0]">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1A1A1A] mb-8">Send Us a Message</h2>

          {submitStatus && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="submit-status">
              {submitStatus.type === 'success' ? <CheckCircle size={24} className="text-green-600" /> : <AlertCircle size={24} className="text-red-600" />}
              <span>{submitStatus.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <FormInput label="Your Name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder="John Doe" />
              <FormInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Inquiry Type *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {INQUIRY_TYPES.map((type) => (
                  <InquiryTypeButton key={type.value} type={type} isSelected={formData.inquiry_type === type.value} onChange={handleChange} />
                ))}
              </div>
            </div>

            <FormInput label="Subject" name="subject" type="text" value={formData.subject} onChange={handleChange} placeholder="What&apos;s this about?" />
            <FormTextarea label="Your Message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us more about your inquiry..." />

            <button type="submit" disabled={isSubmitting} className="w-full btn-primary px-8 py-4 rounded-full text-lg font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-button">
              {isSubmitting ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</> : <><Send size={20} />Send Message</>}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-[#E2DCD0] text-center">
            <p className="text-[#5C5C5C]">Or email us directly at <a href="mailto:everything@8eight8.net" className="text-[#E07A5F] hover:underline font-medium" data-testid="direct-email-link">everything@8eight8.net</a></p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormInput = ({ label, name, type, value, onChange, placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-[#1A1A1A] mb-2">{label} *</label>
    <input type={type} id={name} name={name} value={value} onChange={onChange} required className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent transition-all" placeholder={placeholder} data-testid={`input-${name}`} />
  </div>
);

const FormTextarea = ({ label, name, value, onChange, placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-[#1A1A1A] mb-2">{label} *</label>
    <textarea id={name} name={name} value={value} onChange={onChange} required rows={6} className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent transition-all resize-none" placeholder={placeholder} data-testid={`input-${name}`} />
  </div>
);

// Page Components
const ContactPage = () => (
  <div className="min-h-screen bg-[#F9F6F0]">
    <Navbar isHomePage={false} />
    <ContactHero />
    <OpportunitiesSection />
    <ContactForm />
    <Footer />
  </div>
);

const Home = () => (
  <div className="min-h-screen">
    <Navbar isHomePage={true} />
    <HeroSection />
    <SynopsisSection />
    <AuthorsSection />
    <FAQSection />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/order-success" element={<OrderSuccessPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
